import { useEffect } from 'react';

interface UseSEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
}

export function useSEO({ title, description, keywords, canonicalUrl }: UseSEOProps) {
  useEffect(() => {
    const siteName = '蓝导航';
    const fullTitle = title.includes(siteName) ? title : `${title} - ${siteName}`;

    document.title = fullTitle;

    const metaTags: Record<string, { name?: string; property?: string; content: string }> = {
      description: { name: 'description', content: description },
      keywords: { name: 'keywords', content: keywords },
      'og:title': { property: 'og:title', content: fullTitle },
      'og:description': { property: 'og:description', content: description },
      'twitter:title': { name: 'twitter:title', content: fullTitle },
      'twitter:description': { name: 'twitter:description', content: description },
    };

    Object.entries(metaTags).forEach(([key, attrs]) => {
      let el: HTMLMetaElement | null = null;
      if (attrs.name) {
        el = document.querySelector(`meta[name="${attrs.name}"]`);
      } else if (attrs.property) {
        el = document.querySelector(`meta[property="${attrs.property}"]`);
      }
      if (el) {
        el.setAttribute('content', attrs.content);
      }
    });

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (link) {
        link.href = canonicalUrl;
      } else {
        link = document.createElement('link');
        link.rel = 'canonical';
        link.href = canonicalUrl;
        document.head.appendChild(link);
      }
    }

    const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    return () => {
      const ld = document.querySelector('script[type="application/ld+json"]');
      if (ld) ld.remove();
    };
  }, [title, description, keywords, canonicalUrl]);
}

export function injectJSONLD(json: Record<string, unknown>) {
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(json);
  document.head.appendChild(script);
}